/**
 * Tempo section of the Song & Timing workspace (W2-08 timing; DESIGN.md §4.15 "TEMPO", §5.2 "Tempo"):
 *  - the BPM field of the song tempo (the tempo point at beat 0): typed decimals, refuses anything that is not a
 *    finite BPM > 0 (FAQ-4c), arrows +-0.1, Ctrl (or Alt) +-0.01, Shift +-1, the same steps on the -/+ buttons and
 *    on the wheel while focused; bursts of steps are one undo entry;
 *  - Detect with candidates and the octave fixes (TempoDetect);
 *  - tap tempo (TapTempo; the B key runs the same `timing.tap` command).
 * `BpmField` is shared with the wizard Beat step.
 */

import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { AppServices } from '../../app/registry/types';
import { IconButton, Icon } from '../../ui';
import { m } from './timing.messages';
import type { SongAnalysis } from './timingAnalysis';
import { timingAnalysisFor } from './timingAnalysis';
import { multiplyTempo, setSongBpm } from './timingEdits';
import type { TimingView } from './timingDoc';
import type { TimingText } from './timingHooks';
import { bpmStep, formatBpm, parseBpm, readTaps, steppedBpm } from './timingModel';
import { useTimingSession } from './timingSession';
import { TapTempo } from './TapTempo';
import { TempoDetect } from './TempoDetect';
import { resetTaps, tapNow } from './tapActions';
import s from './Timing.module.css';

export interface BpmFieldProps {
  readonly text: TimingText;
  readonly value: number | null;
  /** `step`: the change came from an arrow, a button or the wheel (callers coalesce those). */
  readonly onCommit: (bpm: number, step: boolean) => void;
  readonly disabled?: boolean;
  readonly label?: string;
}

/** The BPM input with its -/+ buttons (see the module header). */
export function BpmField(props: BpmFieldProps) {
  const { text, value } = props;
  const id = useId();
  const errId = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const parsed = draft === null ? value : parseBpm(draft);
  const invalid = draft !== null && parsed === null;
  const disabled = props.disabled === true || value === null;
  const commitRef = useRef(props.onCommit);
  useEffect(() => {
    commitRef.current = props.onCommit;
  });

  const step = useCallback(
    (dir: 1 | -1, mod: Parameters<typeof bpmStep>[0]): void => {
      const base = parsed ?? value;
      if (base === null) return;
      const next = steppedBpm(base, dir, bpmStep(mod));
      if (next === null) return;
      setDraft(null);
      commitRef.current(next, true);
    },
    [parsed, value],
  );

  // Wheel steps while the field has focus (a native listener: React's wheel handlers are passive).
  useEffect(() => {
    const el = inputRef.current;
    if (el === null) return undefined;
    const onWheel = (e: WheelEvent): void => {
      if (document.activeElement !== el || e.deltaY === 0) return;
      e.preventDefault();
      step(e.deltaY < 0 ? 1 : -1, { shiftKey: e.shiftKey, altKey: e.altKey, ctrlKey: false });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, [step]);

  const commitDraft = (): void => {
    if (draft === null) return;
    const v = parseBpm(draft);
    if (v !== null && v !== value) props.onCommit(v, false);
    if (v !== null) setDraft(null);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      step(e.key === 'ArrowUp' ? 1 : -1, e);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Escape' && draft !== null) {
      e.preventDefault();
      e.stopPropagation();
      setDraft(null);
    }
  };

  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={id}>
        {props.label ?? text.t(m.bpmLabel)}
      </label>
      <div className={s.rowTight}>
        <IconButton
          icon="Minus"
          size="sm"
          label={{ key: m.bpmDown }}
          disabledReason={disabled ? { key: m.noBpmReason } : null}
          onClick={(e) => {
            step(-1, e);
          }}
        />
        <input
          ref={inputRef}
          id={id}
          className={`${s.input} ${s.bpmInput}`}
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          value={draft ?? (value === null ? '' : formatBpm(value))}
          disabled={disabled}
          aria-invalid={invalid}
          aria-describedby={invalid ? errId : undefined}
          data-testid="timing-bpm"
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            commitDraft();
            setDraft(null);
          }}
        />
        <IconButton
          icon="Plus"
          size="sm"
          label={{ key: m.bpmUp }}
          disabledReason={disabled ? { key: m.noBpmReason } : null}
          onClick={(e) => {
            step(1, e);
          }}
        />
      </div>
      {invalid ? (
        <p id={errId} className={s.error} role="alert">
          <Icon name="CircleAlert" size={14} />
          {text.t(m.bpmInvalid)}
        </p>
      ) : (
        <p className={s.help}>{text.t(m.bpmHelp)}</p>
      )}
    </div>
  );
}

export interface TempoPanelProps {
  readonly services: AppServices;
  readonly text: TimingText;
  readonly view: TimingView;
  readonly analysis: SongAnalysis | null;
}

const selectTaps = (st: { readonly taps: Parameters<typeof readTaps>[0] }) => st.taps;

export function TempoPanel(props: TempoPanelProps) {
  const { services, text, view, analysis } = props;
  const first = view.points[0] ?? null;
  const taps = useTimingSession(services, selectTaps);
  const reading = readTaps(taps);
  const chord = services.keymap.bindingsFor('timing.tap')[0] ?? null;
  const audio = view.music?.id ?? null;

  const detect = (): void => {
    if (audio === null) return;
    timingAnalysisFor(services)
      .detectTempo(audio, { force: analysis?.candidates !== null && analysis?.candidates !== undefined })
      .catch((err: unknown) => {
        services.ui.toast(
          { key: m.analysisFailed, params: { error: err instanceof Error ? err.message : String(err) } },
          { kind: 'error' },
        );
      });
  };

  return (
    <div className={s.stack}>
      <BpmField
        text={text}
        value={first?.bpm ?? null}
        onCommit={(bpm, step) => {
          setSongBpm(services, bpm, step ? 'timing.bpmStep' : undefined);
        }}
      />
      {view.points.length > 1 && <p className={s.help}>{text.t(m.bpmMultiple)}</p>}
      <TempoDetect
        text={text}
        candidates={analysis?.candidates ?? null}
        detecting={analysis?.detecting === true}
        progress={analysis?.detecting === true && analysis.task === 'tempo' ? analysis.progress : null}
        error={analysis?.error ?? null}
        currentBpm={first?.bpm ?? null}
        disabledReason={audio === null ? m.noSongReason : null}
        onDetect={detect}
        onUse={(bpm) => {
          setSongBpm(services, bpm);
        }}
        onMultiply={(factor) => {
          multiplyTempo(services, factor);
        }}
      />
      <TapTempo
        text={text}
        reading={reading}
        chord={chord}
        onTap={() => {
          tapNow(services);
        }}
        onUse={(bpm) => {
          setSongBpm(services, bpm);
          resetTaps(services);
        }}
        onReset={() => {
          resetTaps(services);
        }}
      />
    </div>
  );
}
